<template>
  <div class="crypkit-otp-input" :class="{ 'has-error': !!error }">
    <input
      v-for="(_, index) in length"
      :key="index"
      :ref="
        (el) => {
          if (el) inputs[index] = el as HTMLInputElement
        }
      "
      type="text"
      inputmode="numeric"
      autocomplete="one-time-code"
      pattern="[0-9]*"
      maxlength="1"
      :disabled="disabled"
      :value="digits[index] ?? ''"
      @input="(e) => handleInput(e, index)"
      @keydown="(e) => handleKeydown(e, index)"
      @paste="handlePaste"
      @focus="handleFocus"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    modelValue: string
    length?: number
    disabled?: boolean
    error?: string | null
    autofocus?: boolean
  }>(),
  {
    length: 6,
    disabled: false,
    error: null,
    autofocus: true,
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'complete', value: string): void
}>()

const inputs = ref<HTMLInputElement[]>([])

const digits = computed(() => {
  const arr: string[] = []
  for (let i = 0; i < props.length; i++) {
    arr.push(props.modelValue[i] ?? '')
  }
  return arr
})

function emitValue(newDigits: string[]) {
  const value = newDigits.join('')
  emit('update:modelValue', value)
  if (value.length === props.length && /^\d+$/.test(value)) {
    emit('complete', value)
  }
}

function fillDigits(raw: string, startIndex: number): string[] | null {
  const sanitized = raw.replace(/\D/g, '').slice(0, props.length - startIndex)
  if (!sanitized) return null

  const next = [...digits.value]
  for (let i = 0; i < sanitized.length; i++) {
    next[startIndex + i] = sanitized[i]
  }
  emitValue(next)

  focusInput(Math.min(startIndex + sanitized.length, props.length - 1))
  return next
}

function handleInput(event: Event, index: number) {
  const target = event.target as HTMLInputElement
  const raw = target.value.replace(/\D/g, '')

  if (raw.length === 0) {
    const next = [...digits.value]
    next[index] = ''
    target.value = ''
    emitValue(next)
    return
  }

  // Multi-char input — covers browser autofill that lands the full code in one field.
  if (raw.length > 1) {
    const next = fillDigits(raw, index)
    if (next) target.value = next[index] ?? ''
    return
  }

  const next = [...digits.value]
  next[index] = raw
  target.value = raw
  emitValue(next)

  if (index < props.length - 1) {
    focusInput(index + 1)
  }
}

function handleKeydown(event: KeyboardEvent, index: number) {
  if (event.key === 'Backspace' && !digits.value[index] && index > 0) {
    focusInput(index - 1)
  } else if (event.key === 'ArrowLeft' && index > 0) {
    event.preventDefault()
    focusInput(index - 1)
  } else if (event.key === 'ArrowRight' && index < props.length - 1) {
    event.preventDefault()
    focusInput(index + 1)
  }
}

function handlePaste(event: ClipboardEvent) {
  event.preventDefault()
  fillDigits(event.clipboardData?.getData('text') ?? '', 0)
}

function handleFocus(event: FocusEvent) {
  ;(event.target as HTMLInputElement).select()
}

function focusInput(index: number) {
  nextTick(() => {
    inputs.value[index]?.focus()
    inputs.value[index]?.select()
  })
}

function focusFirst() {
  focusInput(0)
}

watch(
  () => props.autofocus,
  (v) => {
    if (v) nextTick(() => focusFirst())
  },
  { immediate: true }
)

// When parent resets the value (e.g. after an error), ensure the first input
// gets focus again for a fresh attempt.
watch(
  () => props.modelValue,
  (val, prev) => {
    if (prev && !val) focusFirst()
  }
)
</script>

<style lang="scss">
@reference '../../../assets/style/main.scss';

.crypkit-otp-input {
  @apply flex gap-2;

  input {
    @apply w-10 h-control text-center text-lg font-semibold border border-outline rounded-md bg-surface text-primary transition-colors;

    &:focus {
      @apply outline-none border-brand;
    }

    &:disabled {
      @apply opacity-50 cursor-not-allowed;
    }
  }

  &.has-error input {
    @apply border-negative;
  }
}
</style>
